import {
  Component,
  h,
  Prop,
  State,
  Watch,
  EventEmitter,
  Event,
  Listen,
  Element,
} from '@stencil/core';
import { getClosingHourFromBank } from '../../../global/utility/get-closing-hour-from-bank';
import store from '../../../global/store';
import { tracker } from '../../../global/tracker';
import { BankSearchMode, SearchResponseEntry } from '../../../../types/global'; // eslint-disable-line import/extensions

@Component({
  tag: 'ui-bank-search-list-item',
  styleUrl: 'ui-bank-search-list-item.scss',
})
export class UiBankSearchListItem {
  @Element() el: HTMLElement;

  @Prop() bank: SearchResponseEntry;

  @Prop() index: number;

  @Prop() mode: BankSearchMode;

  @State() isHovered = false;

  @State() isSelected = false;

  @State() closing: string;

  @Event({
    cancelable: true,
    bubbles: true,
  })
  bankDetailLoaded: EventEmitter;

  linkOnlineBanking: HTMLElement;

  @Listen('overlayVisibilityChange', { target: 'window' })
  handleOverlayVisibilityChange(event: CustomEvent<{ isVisible: boolean }>) {
    if (
      !event.detail.isVisible &&
      store.state.selectedBank?.id === this.bank.id
    ) {
      this.el.focus(); // Refocus selected bank after overlay closes
    }
  }

  componentWillLoad() {
    if (this.isSelected) {
      store.state.selectedResultIndex = this.index;
    }

    store.onChange(
      'hoveredResultIndex',
      this.onHoveredResultIndexChange.bind(this)
    );
    store.onChange('selectedBank', this.onSelectedBankChange.bind(this));

    // store.onChange will not fire during init; update this.isSelected manually:
    this.onSelectedBankChange();
  }

  componentDidLoad() {
    this.bankDetailLoaded.emit(this);
  }

  private onHoveredResultIndexChange() {
    this.isHovered = store.state.hoveredResultIndex === this.index;
  }

  private onSelectedBankChange() {
    this.isSelected = store.state.selectedBank?.id === this.bank.id;
  }

  private onBankDetailsClick() {
    store.state.selectedResultIndex = this.index;
    store.state.selectedBank = this.bank;
    if (this.mode === 'regular') {
      store.state.isOverlayVisible = true;
      tracker.trackBankSelect('Teaser');
      tracker.trackInitialFormPage(this.bank?.bankCode, this.bank?.name);
    }
  }

  private onOnlineBankingClick() {
    tracker.trackBankSelect('Link', 'light');
    tracker.trackInitialFormPage(this.bank?.bankCode, this.bank?.name);
  }

  @Watch('bank')
  setClosingHour() {
    this.closing = getClosingHourFromBank(this.bank);
  }

  render() {
    return (
      <div role="listitem">
        {this.mode === 'regular'
          ? this.renderDetailsRegular()
          : this.renderDetailsLight()}
      </div>
    );
  }

  private onPhoneLinkClick(event) {
    event.stopPropagation(); // prevents from bubbling up and opening the item-details
    tracker.trackBankPhoneLink();
  }

  private renderDetailsRegular() {
    return (
      <div
        class="bank-search-list-item-container cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => this.onBankDetailsClick()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            this.onBankDetailsClick();
          }
        }}
        onMouseEnter={() => {
          store.state.hoveredResultIndex = this.index;
        }}
        onMouseLeave={() => {
          store.state.hoveredResultIndex = null;
        }}
      >
        <div
          class={{
            'bank-details bank-details--regular': true,
            selected: this.isSelected,
            hover: this.isHovered,
          }}
        >
          {this.renderLogo()}
          <div class="bank-details__info">
            <p class="sd-headline sd-headline--size-base">{this.bank.name}</p>
            <p class="sd-paragraph sd-paragraph--size-sm">
              {this.bank.street}
              <br />
              {this.bank.zipCode} {this.bank.city}
            </p>
            {this.bank.phoneArea && this.bank.phoneNumber !== 'null' && (
              <p class="sd-paragraph sd-paragraph--size-sm">
                <sd-link
                  href={`tel:${this.bank.phoneArea}${this.bank.phoneNumber}`}
                  onClick={this.onPhoneLinkClick.bind(this)}
                >
                  <sd-icon
                    library={(window as any).ui.cmsState?.iconLibrary}
                    name="union-investment/system/phone"
                    slot="icon-left"
                  ></sd-icon>
                  {this.bank.phoneArea} {this.bank.phoneNumber}
                </sd-link>
              </p>
            )}
            {this.closing && (
              <p class="sd-paragraph sd-paragraph--size-sm bank-details__info__closing">
                Geöffnet: Heute bis {this.closing} Uhr
              </p>
            )}
          </div>
          {this.renderDistance()}
        </div>
      </div>
    );
  }

  private renderDetailsLight() {
    return (
      <div class="bank-search-list-item-container">
        <div class="bank-details bank-details--light">
          {this.renderLogo()}
          <div class="bank-details__info">
            <h4 class="sd-headline sd-headline--size-base">{this.bank.name}</h4>
            <p class="sd-paragraph sd-paragraph--size-sm">
              {this.bank.zipCode} {this.bank.city}
            </p>
          </div>
          {this.renderOnlineBanking(this.bank.name)}
        </div>
      </div>
    );
  }

  private renderLogo() {
    return (
      <div class="bank-details__logo">
        <ui-cms-image
          src={
            this.bank.thumbnail || `${store.state.assetsUrl}/bank-fallback.png`
          }
        ></ui-cms-image>
      </div>
    );
  }

  private renderDistance() {
    return (
      this.bank.distance !== 0 && (
        <div class="bank-details__distance">
          <p class="sd-paragraph sd-paragraph--size-sm">
            {`${this.bank.distance},00 km`}
          </p>
        </div>
      )
    );
  }

  private renderOnlineBanking(name: string) {
    return (
      this.bank.urlOnlineBanking && (
        <div class="bank-details__onlinebanking">
          <p class="sd-paragraph sd-paragraph--size-sm">
            <sd-link
              ref={(el) => {
                this.linkOnlineBanking = el as HTMLElement;
              }}
              href={this.bank.urlOnlineBanking}
              target="_blank"
              onClick={() => {
                this.onOnlineBankingClick();
              }}
            >
              <sd-icon
                library={(window as any).ui.cmsState?.iconLibrary}
                name="union-investment/system/chevron-right"
                slot="icon-right"
              ></sd-icon>
              {(window as any).ui.services?.i18n?.translate('bankSearch')
                .onlineBankingLinkText || 'Zum Onlinebanking'}
              <span class="sr-only">der {name}</span>
            </sd-link>
          </p>
        </div>
      )
    );
  }
}
